import { ArrowLeft, MessageCircle, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  deleteSiteGroup,
  deleteTenantGroup,
  getTenantCometchatSettings,
  syncSiteGroup,
  syncTenantGroup,
  updateTenantCometchatSettings,
} from '../../../services/cometchatApi';
import {
  fetchGroupListWithMeta,
  formatCometchatApiError,
  getTenantShiftEnabled,
  groupHasChat,
  loadCometchatPermissions,
  mapGroupToRow,
} from '../cometchatUtils';
import './ChatGroupManagement.css';

const ChatGroupManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canManageChatAdmin, setCanManageChatAdmin] = useState(true);
  const [tenantShiftEnabled, setTenantShiftEnabled] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [togglingTenantShift, setTogglingTenantShift] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [rawGroups, tenantSettings] = await Promise.all([
        fetchGroupListWithMeta(),
        getTenantCometchatSettings().catch(() => null),
      ]);

      setTenantShiftEnabled(getTenantShiftEnabled(tenantSettings));

      const rows = rawGroups
        .map(group => mapGroupToRow(group))
        .sort((a, b) => {
          const aHasGroup = groupHasChat(a);
          const bHasGroup = groupHasChat(b);
          if (aHasGroup !== bHasGroup) return aHasGroup ? -1 : 1;
          if (a.level !== b.level) {
            return a.level === 'organization' ? -1 : 1;
          }
          return (a.group_name || '').localeCompare(b.group_name || '');
        });

      setGroups(rows);
    } catch (fetchError) {
      console.error('Failed to load chat groups:', fetchError);
      setError(fetchError.message || 'Failed to load chat groups.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCometchatPermissions()
      .then(permissions => {
        setCanManageChatAdmin(permissions.canManageChatAdmin);
      })
      .catch(permError => {
        console.error('Failed to load permissions:', permError);
      });
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (!location.state?.refresh) return;

    fetchGroups();
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, fetchGroups]);

  const handleEnableChat = group => {
    navigate('/onboarding-site-onboarding/chat/create', {
      state: {
        level: group.level === 'organization' ? 'organization' : 'site',
        siteId: group.site_id || '',
      },
    });
  };

  const handleToggleTenantShift = async () => {
    setTogglingTenantShift(true);
    setError(null);
    try {
      await updateTenantCometchatSettings(!tenantShiftEnabled);
      setTenantShiftEnabled(prev => !prev);
    } catch (toggleError) {
      setError(formatCometchatApiError(toggleError));
    } finally {
      setTogglingTenantShift(false);
    }
  };

  const handleSync = async group => {
    setActionId(group.id);
    setError(null);
    try {
      if (group.level === 'organization') {
        await syncTenantGroup();
      } else {
        await syncSiteGroup(group.site_id);
      }
      await fetchGroups();
    } catch (syncError) {
      setError(formatCometchatApiError(syncError));
    } finally {
      setActionId(null);
    }
  };

  const handleDisable = async group => {
    const label = group.group_name || group.site_name;
    const confirmed = window.confirm(
      `Disable chat for "${label}"? Members will lose access until an admin re-enables chat.`
    );
    if (!confirmed) return;

    setActionId(group.id);
    setError(null);
    try {
      if (group.level === 'organization') {
        await deleteTenantGroup();
      } else {
        await deleteSiteGroup(group.site_id);
      }
      await fetchGroups();
    } catch (deleteError) {
      setError(formatCometchatApiError(deleteError));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="chat-group-page">
      <div className="chat-group-header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate('/onboarding-site-onboarding')}
          aria-label="Back to site onboarding"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="chat-group-header-text">
          <h1 className="chat-group-title">Chat Group Management</h1>
          <p className="chat-group-subtitle">
            Create and manage CometChat groups for your organization and sites.
          </p>
        </div>
        <button
          type="button"
          className="chat-group-create-btn"
          onClick={() => navigate('/onboarding-site-onboarding/chat/create')}
        >
          <Plus className="w-4 h-4" />
          Create Group
        </button>
      </div>

      {!loading && (
        <div className="chat-group-tenant-shift">
          <span>
            Tenant shift sync:{' '}
            <strong>{tenantShiftEnabled ? 'ON' : 'OFF'}</strong>
            {!tenantShiftEnabled && (
              <span className="chat-group-tenant-shift-hint">
                {' '}
                — required for scheduler-based site groups.
              </span>
            )}
          </span>
          {canManageChatAdmin && (
            <button
              type="button"
              className="chat-group-tenant-shift-btn"
              disabled={togglingTenantShift}
              onClick={handleToggleTenantShift}
            >
              {togglingTenantShift
                ? 'Updating...'
                : tenantShiftEnabled
                  ? 'Turn off'
                  : 'Turn on'}
            </button>
          )}
        </div>
      )}

      <div className="chat-group-content">
        {loading ? (
          <p className="chat-group-empty">Loading chat groups...</p>
        ) : error ? (
          <p className="chat-group-empty" role="alert">
            {error}
          </p>
        ) : groups.length === 0 ? (
          <div className="chat-group-empty-state">
            <p>No sites have chat enabled yet.</p>
            <button
              type="button"
              className="chat-group-create-btn"
              onClick={() => navigate('/onboarding-site-onboarding/chat/create')}
            >
              <Plus className="w-4 h-4" />
              Create Group
            </button>
          </div>
        ) : (
          <table className="chat-group-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Level</th>
                <th>Status</th>
                <th>Shift sync</th>
                <th>Last sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <tr key={group.id}>
                  <td>
                    <span className="inline-flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-[#0a8089]" />
                      {group.group_name || group.site_name}
                    </span>
                  </td>
                  <td>
                    {group.level === 'organization' ? 'Organization' : 'Site'}
                  </td>
                  <td>
                    <span
                      className={`chat-group-status ${
                        group.group_guid ? 'active' : 'inactive'
                      }`}
                    >
                      {group.group_guid ? 'Active' : 'Not created'}
                    </span>
                  </td>
                  <td>{group.shift_sync_active ? 'On' : 'Off'}</td>
                  <td>
                    {group.last_sync_status === 'error'
                      ? 'Sync error'
                      : group.last_synced_at || '—'}
                  </td>
                  <td>
                    {group.group_guid ? (
                      <div className="chat-group-row-actions">
                        <button
                          type="button"
                          className="chat-group-action-btn"
                          title="Sync now"
                          disabled={actionId === group.id}
                          onClick={() => handleSync(group)}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="chat-group-action-btn danger"
                          title="Disable chat"
                          disabled={actionId === group.id}
                          onClick={() => handleDisable(group)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="chat-group-enable-btn"
                        onClick={() => handleEnableChat(group)}
                      >
                        Enable
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ChatGroupManagement;
